import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/ui/PageHeader';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { IconPlus } from '../../components/ui/Icons';
import { useToast } from '../../contexts/ToastContext';
import { usePortal } from '../../contexts/PortalContext';
import { getTenantId } from '../../utils/ids';
import { emptyTenantForm, formToTenantPayload, tenantToForm } from '../../utils/tenantForm';
import { copyText, normalizeHost } from '../../utils/portalMode';
import { TenantFormFields } from '../Tenants';

function Customers() {
  const toast = useToast();
  const { partnerPortalHost } = usePortal();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({ ...emptyTenantForm, kind: 'customer' });
  const [domainVerify, setDomainVerify] = useState(null);
  const [domainVerifyLoading, setDomainVerifyLoading] = useState(false);

  const buildSignupOrigin = () => {
    const host = normalizeHost(partnerPortalHost || window.location.hostname);
    const parts = host.split('.');
    if (parts.length < 2) {
      return window.location.origin;
    }
    const baseHost = parts[0] === 'partner' ? parts.slice(1).join('.') : host;
    const protocol = baseHost.includes('localhost') ? 'http' : 'https';
    return `${protocol}://${baseHost}`;
  };

  const buildJoinLink = (tenant) => {
    if (tenant.domain) {
      const host = normalizeHost(tenant.domain);
      const protocol = host.includes('localhost') ? 'http' : 'https';
      return `${protocol}://${host}/signup?targetTenantId=${encodeURIComponent(getTenantId(tenant))}&mode=join_existing_customer`;
    }
    return `${buildSignupOrigin()}/signup?targetTenantId=${encodeURIComponent(getTenantId(tenant))}&mode=join_existing_customer`;
  };

  const buildCreateLink = (tenant) => (
    `${buildSignupOrigin()}/signup?partnerTenantId=${encodeURIComponent(tenant.partnerId || '')}&mode=partner_new_customer`
  );

  const handleCopyLink = async (value, label) => {
    const ok = await copyText(value);
    if (ok) {
      toast.success(`${label} copied`);
    } else {
      toast.error(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  const handleCopyJoinLink = async (tenant) => {
    try {
      const response = await api.post('/partner/customers/join-link', { tenantId: getTenantId(tenant) });
      const joinToken = response.data?.data?.joinToken;
      if (!joinToken) throw new Error('missing token');
      await handleCopyLink(`${buildJoinLink(tenant)}&joinToken=${encodeURIComponent(joinToken)}`, 'Join link (valid for 7 days)');
    } catch {
      toast.error('Failed to create join link');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.post('/partner/customers/list', { page: 1, limit: 500 });
      setTenants(response.data.data?.tenants || []);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleCreate = () => {
    setEditingTenant(null);
    setFormData({ ...emptyTenantForm, kind: 'customer' });
    setShowModal(true);
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({ ...tenantToForm(tenant), kind: 'customer' });
    setDomainVerify(tenant.domainVerification || null);
    setShowModal(true);
  };

  const handleStartDomainVerification = async () => {
    if (!editingTenant) return;
    setDomainVerifyLoading(true);
    try {
      const res = await api.post('/partner/domain-verification/start', { tenantId: getTenantId(editingTenant) });
      setDomainVerify(res.data.data);
      toast.success('Verification started. Add DNS records then click Check.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start verification');
    } finally {
      setDomainVerifyLoading(false);
    }
  };

  const handleCheckDomainVerification = async () => {
    if (!editingTenant) return;
    setDomainVerifyLoading(true);
    try {
      const res = await api.post('/partner/domain-verification/check', { tenantId: getTenantId(editingTenant) });
      setDomainVerify(res.data.data);
      if (res.data.data.status === 'verified') {
        toast.success('Domain verified!');
      } else {
        toast.info('DNS not matching yet. Try again after propagation.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification check failed');
    } finally {
      setDomainVerifyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = formToTenantPayload({ ...formData, kind: 'customer' }, { includeSubdomain: !editingTenant });
      if (editingTenant) {
        await api.post('/partner/customers/update', {
          tenantId: getTenantId(editingTenant),
          ...payload,
        });
        toast.success('Customer updated');
      } else {
        await api.post('/partner/customers/create', payload);
        toast.success('Customer created');
      }
      setShowModal(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save customer');
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading customers..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Customers"
        description="Workspaces you resell. They see your brand on their login."
        action={
          <button type="button" onClick={handleCreate} className="btn btn--primary">
            <IconPlus width={18} height={18} />
            Add customer
          </button>
        }
      />
      <div className="card">
        {tenants.length === 0 ? (
          <EmptyState title="No customers yet" description="Create a customer workspace under your partner account." />
        ) : (
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Subdomain</th>
                  <th>Retail collector</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr key={getTenantId(tenant)}>
                    <td data-label="Name">{tenant.name}</td>
                    <td data-label="Subdomain">{tenant.subdomain}</td>
                    <td data-label="Retail collector">{tenant.billing?.retailCollector || 'inherit'}</td>
                    <td data-label="Actions">
                      <button type="button" className="btn btn--secondary btn--sm" onClick={() => handleEdit(tenant)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showModal && (
        <Modal
          title={editingTenant ? 'Edit customer' : 'Add customer'}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <TenantFormFields
              formData={{ ...formData, kind: 'customer' }}
              setFormData={setFormData}
              editingTenant={editingTenant}
              partners={[]}
              kindLocked
              hidePartnerSelect
            />
            {editingTenant && formData.domain && (
              <>
                <h3 className="form-section-title">Custom Domain Verification</h3>
                {domainVerify?.status === 'verified' ? (
                  <div className="alert alert--success">Domain <strong>{domainVerify.domain || formData.domain}</strong> is verified.</div>
                ) : (
                  <>
                    {domainVerify?.txtName ? (
                      <div className="alert alert--info">
                        <p><strong>Add this TXT record:</strong></p>
                        <p>Name: <code>{domainVerify.txtName}</code></p>
                        <p>Value: <code>{domainVerify.txtValue || domainVerify.token}</code></p>
                        {(domainVerify.cnameTarget || domainVerify.expectedCnameTarget) && (
                          <>
                            <p style={{ marginTop: '0.5rem' }}><strong>Or add CNAME:</strong></p>
                            <p><code>{formData.domain}</code> &rarr; <code>{domainVerify.cnameTarget || domainVerify.expectedCnameTarget}</code></p>
                          </>
                        )}
                        {domainVerify.lastError && <p className="text-muted" style={{ marginTop: '0.5rem' }}>Last error: {domainVerify.lastError}</p>}
                      </div>
                    ) : (
                      <p className="text-muted">Click Start Verification to generate DNS records for this domain.</p>
                    )}
                    <div className="btn-group" style={{ marginBottom: '1rem' }}>
                      <button type="button" className="btn btn--secondary btn--sm" disabled={domainVerifyLoading} onClick={handleStartDomainVerification}>
                        {domainVerify?.txtName ? 'Restart' : 'Start Verification'}
                      </button>
                      {domainVerify?.txtName && (
                        <button type="button" className="btn btn--primary btn--sm" disabled={domainVerifyLoading} onClick={handleCheckDomainVerification}>
                          Check DNS
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            {editingTenant && (
              <>
                <h3 className="form-section-title">Onboarding Links</h3>
                <div className="form-group">
                  <label className="form-label">Join this workspace</label>
                  <div className="btn-group">
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => handleCopyJoinLink(editingTenant)}>
                      Copy join link
                    </button>
                  </div>
                </div>
                {!!editingTenant.partnerId && (
                  <div className="form-group">
                    <label className="form-label">Create another workspace under this partner</label>
                    <div className="btn-group">
                      <button type="button" className="btn btn--secondary btn--sm" onClick={() => handleCopyLink(buildCreateLink(editingTenant), 'Partner signup link')}>
                        Copy partner signup link
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="modal__footer">
              <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn--primary">{editingTenant ? 'Save' : 'Create'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default Customers;
